import {
  Controller,
  useFieldArray,
  type Control,
  type FieldErrors,
  type FieldValues,
} from "react-hook-form";
import { EliteMetadataConfiguration } from "../EliteMetadataConfiguration";
import { EntityPicker, type Entity } from "../EntityPicker";
import styles from "./SubscriptionMetadataConfig.module.css";

export type SubscriptionMetadataConfigProps = {
  packageId: string | null;
  packageFeatures: Array<{
    entity_id_field: string | null;
    entity_id_label: string | null;
    entity_id_help_text: string | null;
  }> | null;
  metadataQuantities: Record<string, number> | null;
  disabled?: boolean;
  firmId?: number | string | null;
  officeOptions: Entity[];
  editorialCategoryOptions: Array<{ value: number | string; label: string; group?: string }>;
  control: Control<FieldValues>;
  errors?: FieldErrors<FieldValues>;
  practiceGroupIdMap?: Map<string, number | string>;
  selectedPracticeGroups?: (number | string)[];
  onPracticeGroupChange?: (groups: (number | string)[], areas: (number | string)[]) => void;
};

type FieldConfig = {
  key: string;
  label: string;
  helpText: string;
  quantity: number;
  entities: Entity[];
  unbounded: boolean;
  noun: string;
};

function extractEntityNoun(label: string): string {
  const lowerLabel = label.toLowerCase();
  const prefixes = ["select ", "choose ", "pick "];
  let noun = lowerLabel;

  for (const prefix of prefixes) {
    if (lowerLabel.startsWith(prefix)) {
      noun = lowerLabel.substring(prefix.length);
      break;
    }
  }

  noun = noun.endsWith("s") ? noun.slice(0, -1) : noun;
  return noun;
}

export function SubscriptionMetadataConfig({
  packageId,
  packageFeatures,
  metadataQuantities,
  disabled = false,
  firmId,
  officeOptions,
  editorialCategoryOptions,
  control,
  errors = {},
  practiceGroupIdMap,
  selectedPracticeGroups = [],
  onPracticeGroupChange,
}: SubscriptionMetadataConfigProps) {
  // Check if this package requires Elite office/contacts selection (office_contact_pairs only)
  const requiresEliteConfig = metadataQuantities && "office_contact_pairs" in metadataQuantities;
  // 0 means "optional/unlimited", so default to 999
  const rawMax = metadataQuantities?.office_contact_pairs;

  // useFieldArray for proper array field management
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "metadata.office_contact_pairs",
  });

  // Build field configurations from package metadata
  const fieldConfigs: FieldConfig[] = [];

  if (metadataQuantities && packageFeatures) {
    // Group features by entity_id_field to get labels
    const entityFieldLabels = new Map<string, { label: string; helpText: string }>();

    for (const feature of packageFeatures) {
      if (feature.entity_id_field && feature.entity_id_label) {
        entityFieldLabels.set(feature.entity_id_field, {
          label: feature.entity_id_label,
          helpText: feature.entity_id_help_text || "",
        });
      }
    }

    // Custom labels for metadata fields
    const labelMap: Record<string, string> = {
      editorial_category_ids: "Editorial Categories",
    };

    // Create field config for each metadata quantity (except office_contact_pairs which is handled by EliteMetadataConfiguration)
    for (const [fieldName, quantity] of Object.entries(metadataQuantities)) {
      if (fieldName === "office_contact_pairs") continue;

      const fieldConfig = entityFieldLabels.get(fieldName);
      const label = fieldConfig?.label || labelMap[fieldName] || fieldName.replace(/_/g, " ");
      const isUnbounded = fieldName === "publication_edition_ids";
      const noun = extractEntityNoun(label);
      const baseHelpText =
        fieldConfig?.helpText ||
        (isUnbounded ? `Select one or more ${noun}s` : `Select ${quantity} ${label.toLowerCase()}`);

      // Get appropriate entities based on field type
      let entities: Entity[] = [];

      if (fieldName === "editorial_category_ids") {
        // Editorial categories use string UUIDs as values
        entities = editorialCategoryOptions;
      }

      fieldConfigs.push({
        key: fieldName,
        label,
        helpText: baseHelpText,
        quantity,
        entities,
        unbounded: isUnbounded,
        noun,
      });
    }
  }

  // Hide the entire section if no package selected or no metadata needed
  if (!packageId || (fieldConfigs.length === 0 && !requiresEliteConfig)) {
    return null;
  }

  return (
    <section className={styles.container}>
      <h3 className={styles.sectionTitle}>Metadata Configuration</h3>

      <p className={styles.description}>
        Select specific entities for this subscription based on the package configuration
      </p>

      {/* Elite Lawyer Selection - for Elite packages (CHINA-ELITE, GLOBAL-ELITE) with office_contact_pairs */}
      {requiresEliteConfig && (
        <EliteMetadataConfiguration
          firmId={firmId ?? null}
          officeOptions={officeOptions}
          fields={fields}
          isOptional={rawMax === 0}
          disabled={disabled}
          onAppend={append}
          onRemove={remove}
          onUpdate={update}
          errors={errors}
        />
      )}

      {fieldConfigs.length > 0 && (
        <div className={styles.fieldsContainer}>
          {fieldConfigs.map(config => {
            const fieldName = `metadata.${config.key}`;
            const fieldError = errors[fieldName];
            const errorMessage = fieldError?.message ? String(fieldError.message) : undefined;

            return (
              <div key={config.key} className={styles.fieldGroup}>
                <Controller
                  name={fieldName}
                  control={control}
                  rules={{
                    validate: (value: (number | string)[] | undefined) => {
                      const selected = value || [];
                      if (config.unbounded) {
                        if (selected.length < 1) {
                          return `Please select at least one ${config.noun}`;
                        }
                        return true;
                      }
                      if (selected.length !== config.quantity) {
                        return `Please select exactly ${config.quantity} ${config.label.toLowerCase()}${config.quantity !== 1 ? "s" : ""}`;
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => {
                    const isEditorialCategories = config.key === "editorial_category_ids";
                    const enableGroupSelection =
                      isEditorialCategories && !!practiceGroupIdMap && practiceGroupIdMap.size > 0;

                    return (
                      <EntityPicker
                        entities={config.entities}
                        selectedValues={field.value || []}
                        onChange={(values: (number | string)[]) => field.onChange(values)}
                        maxSelections={config.unbounded ? undefined : config.quantity}
                        minSelections={config.unbounded ? 1 : undefined}
                        disabled={disabled}
                        label={config.label}
                        helperText={
                          config.unbounded
                            ? config.helpText
                            : `${config.helpText} (${config.quantity} required)`
                        }
                        error={errorMessage}
                        columns={{ mobile: 1, tablet: 2, desktop: 3 }}
                        compact={false}
                        enableGroupSelection={enableGroupSelection}
                        selectedGroups={enableGroupSelection ? selectedPracticeGroups : undefined}
                        onGroupSelectionChange={
                          enableGroupSelection ? onPracticeGroupChange : undefined
                        }
                        groupIdMap={enableGroupSelection ? practiceGroupIdMap : undefined}
                      />
                    );
                  }}
                />
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
